import { createTecgymAdminAppActivityURL, getAllTecgymAdminAppActivitiesURL } from "./apiUrl";
import { buildAppActivityFields, userNameToSlug } from "./activityLogger";
import { _get, _post } from "./fetch";

const ACTIVITY_LIST_KEYS = [
  "response",
  "data",
  "activities",
  "tecgymAdminAppActivities",
  "appActivities",
  "result",
  "results",
  "items"
];

export function extractAppActivityList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  for (const key of ACTIVITY_LIST_KEYS) {
    const value = data[key];
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      const nestedList = extractAppActivityList(value);
      if (nestedList.length > 0) return nestedList;
    }
  }

  if (
    data.activityType ||
    data.activityBy ||
    data.activityDate ||
    data._id ||
    data.id
  ) {
    return [data];
  }

  return [];
}

export function getAppActivityValue(activity, field) {
  if (!activity || !field) {
    return "";
  }

  const snakeField = field.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  const value = activity[field] ?? activity[snakeField];

  if (value === null || value === undefined) {
    return "";
  }

  return value;
}

export function getAppActivityBy(activity) {
  return String(
    getAppActivityValue(activity, "activityBy") ||
    activity?.name ||
    activity?.userName ||
    ""
  ).trim();
}

export function filterAppActivitiesByUser(activities, userName) {
  const normalizedUserName = String(userName || "").trim().toLowerCase();
  const selectedSlug = userNameToSlug(userName);

  if (!normalizedUserName) {
    return [];
  }

  return activities.filter((activity) => {
    const activityBy = getAppActivityBy(activity);
    const normalizedActivityBy = activityBy.toLowerCase();

    if (!normalizedActivityBy) {
      return false;
    }

    return (
      normalizedActivityBy === normalizedUserName ||
      userNameToSlug(activityBy) === selectedSlug ||
      normalizedActivityBy.startsWith(`${normalizedUserName} `) ||
      normalizedUserName.startsWith(`${normalizedActivityBy} `)
    );
  });
}

export function getUserActivitySuccessMessage(userActivities, userName = "") {
  const count = Array.isArray(userActivities) ? userActivities.length : 0;
  const label = String(userName || "").trim();

  if (count === 0) {
    return label
      ? `No records found for ${label}.`
      : "No records found.";
  }

  return `Activities loaded successfully (${count} record${count === 1 ? "" : "s"}).`;
}

export async function saveAppActivity(activityOptions = {}) {
  try {
    const payload = buildAppActivityFields(activityOptions);
    const response = await _post(createTecgymAdminAppActivityURL(), payload);

    if (response?.success === false || response?.status === false) {
      console.warn(
        "Failed to save app activity:",
        response?.message || response?.error || "Unknown error"
      );
    }

    return response;
  } catch (error) {
    console.warn("Failed to save app activity:", error?.message || error);
    return null;
  }
}

export async function fetchAllTecgymAdminAppActivities() {
  const data = await _get(getAllTecgymAdminAppActivitiesURL(), { useCache: false });

  if (data === null || data === undefined) {
    throw new Error("No response received from activity server.");
  }

  if (data.success === false || data.status === false) {
    const errorMessage =
      (typeof data.error === "string" && data.error.trim()) ||
      (typeof data.message === "string" && data.message.trim()) ||
      "Failed to load app activities.";

    throw new Error(errorMessage);
  }

  const activities = extractAppActivityList(data);

  return {
    activities,
    message: getActivityFetchSuccessMessage(data, activities)
  };
}

function getActivityFetchSuccessMessage(data, activities) {
  const apiMessage = [
    data?.message,
    data?.data?.message,
    data?.response?.message
  ].find((value) => typeof value === "string" && value.trim());

  if (apiMessage) {
    return apiMessage.trim();
  }

  if (activities.length === 0) {
    return "Activities loaded successfully.";
  }

  return `Activities loaded successfully (${activities.length} record${activities.length === 1 ? "" : "s"}).`;
}
